import { RegisterUserAndCustomerRequestModel, SelfRegisterRequestModel, SelfRegisterSecurityBehaviourEnum, WelcomeCouponData } from "../static/easySignup.model";

interface AppSignupRequest {
  username: string;
  password: string;
  nationalIdNumber: string;
  storeCode: string;
}

type SelfRegisterCustomer = {
  card?: string;
  customerName?: string;
  email?: string;
};

export type SelfRegisterResult =
  | { 
      type: "success"; 
      welcomeCoupon: boolean; 
      welcomeCouponData?: WelcomeCouponData | null; 
      customer: SelfRegisterCustomer | null; 
      username: string;
    }
  | { 
      type: "existsSameStore"; 
      message: string; 
      customer: SelfRegisterCustomer | null; 
      username: string;
      mailSent?: boolean;

    }
  | { 
      type: "existsOtherStore"; 
      message: string; 
      customer: SelfRegisterCustomer | null; 
      username: string;
      mailSent?: boolean;
    }
  | { type: "error"; status: number; message: string };

  export type RegisterUserAndCustomerResult =
  | { type: "success"; login: any } // LoginModel
  | { type: "error"; status: number; message: string; code?: string };


export async function selfRegisterUser(data: SelfRegisterRequestModel): Promise<SelfRegisterResult> {

  const url = `${process.env.REACT_APP_API_URL}/v3/customer/selfRegister`;

  try {
    // ===================== FOR TEST =============================
    // FORCE SECURITY BEHAVIOUR 
    // Cambia questo numero da 0 a 3 per testare le diverse schermate!
    // 0 = MaxSecurity, 1 = ViewCardOnly, 2 = ViewAnUpdateMissingData, 3 = ViewAnUpdateAllData
    // ============================================================
    // let MOCK_SECURITY_LEVEL: any = SelfRegisterSecurityBehaviourEnum.ViewAnUpdateMissingData; 

    // return {
    //   type: "existsSameStore",
    //   message: "Test di simulazione: Utente già presente nei sistemi.",
    //   username: MOCK_SECURITY_LEVEL === SelfRegisterSecurityBehaviourEnum.ViewAnUpdateAllData ? "" : "test_user", 
    //   securityBehaviour: MOCK_SECURITY_LEVEL,
    //   customer: {
    //     card: "1234567890123",
    //     customerName: "Mario Rossi",
    //     email: "mario.rossi@example.com"
    //   }
    // };
    // ==================== END FOR TEST ==========================

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    const contentType = response.headers.get("content-type") || "";
    let payload: any = null;

    if (contentType.includes("application/json")) {
      payload = await response.json().catch(() => null);
    } else {
      payload = await response.text().catch(() => "");
    }

    // 200: nuova registrazione ok
    if (response.status === 200 && payload && typeof payload === "object") {
      return {
        type: "success",
        welcomeCoupon: !!payload.welcomeCoupon,
        welcomeCouponData: payload.welcomeCouponData ?? null,
        customer: payload.customer,
        username: payload.username ?? "",
      };
    }

    // 302: cliente già esistente nello stesso store
    if (response.status === 302 && payload && typeof payload === "object") {
      return {
        type: "existsSameStore",
        message:
          payload.message ??
          "Cliente già esistente. La card è stata inviata all'email registrata.",
        customer: payload.customer,
        username: payload.username ?? "",
        mailSent: !!payload.mailSent
      };
    }

    // 303: cliente esistente in altro store del gruppo
    if (response.status === 303 && payload && typeof payload === "object") {
      return {
        type: "existsOtherStore",
        message:
          payload.message ??
          "Cliente già esistente in altro store del gruppo. La card è stata inviata all'email registrata.",          
        customer: payload.customer,
        username: payload.username ?? "",
        mailSent: !!payload.mailSent
      };
    }

    // 4xx/409/altro: testo semplice dal backend o fallback
    return {
      type: "error",
      status: response.status,
      message:
        typeof payload === "string" && payload
          ? payload
          : `Errore ${response.status}`,
    };
  } catch (error) {
    console.error("Errore nella chiamata selfRegisterUser", error);
    return {
      type: "error",
      status: 0,
      message: "Errore di rete o server non raggiungibile",
    };
  }
}

// ************************** REGISTRAZIONE UTENTE ********************************
export async function registerUserAndCustomer(data: RegisterUserAndCustomerRequestModel): Promise<RegisterUserAndCustomerResult> {

  const url = `${process.env.REACT_APP_API_URL}/v3/account/registerUserAndCustomer`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    const contentType = response.headers.get("content-type") || "";
    let payload: any = null;

    if (contentType.includes("application/json")) {
      payload = await response.json().catch(() => null);
    } else {
      payload = await response.text().catch(() => "");
    }

    if (response.ok) {
      // 200 OK: LoginModel
      return { type: "success", login: payload };
    }

    // 400 con ErrorModel { Code, Description }
    if (response.status === 400 && payload && typeof payload === "object") {
      return {
        type: "error",
        status: 400,
        message: payload.description ?? "Richiesta non valida",
        code: payload.code,
      };
    }

    return {
      type: "error",
      status: response.status,
      message:
        typeof payload === "string" && payload
          ? payload
          : `Errore ${response.status}`,
    };
  } catch (error) {
    console.error("Errore nella chiamata registerUserAndCustomer", error);
    return {
      type: "error",
      status: 0,
      message: "Errore di rete o server non raggiungibile",
    };
  }
}


// // REGISTRAZIONE ALL'APP
export async function registerAppAccount(data: AppSignupRequest): Promise<boolean> {
  try {
    const response = await fetch("v3/account/registerUserAndCustomer", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    return response.ok;
  } catch (error) {
    console.error("Errore registrazione app:", error);
    return false;
  }
}